import axios from 'axios';
import { apiCache } from '../utils/apiCache';

const getBaseURL = () => {
  const envUrl = import.meta.env.VITE_API_URL;
  if (envUrl && envUrl.trim() !== '') {
    const trimmed = envUrl.trim().replace(/\/+$/, '');
    return trimmed.endsWith('/api') ? trimmed : `${trimmed}/api`;
  }
  return '/api';
};

const api = axios.create({
  baseURL: getBaseURL(),
  timeout: 30000,
  withCredentials: true
});

// ─── Request Interceptor: Token + Client-Side SWR Caching ────────────────────
api.interceptors.request.use(
  (config) => {
    // 1. Auth header injection
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    const method = (config.method || 'get').toLowerCase();

    // 2. Skip caching for non-GET, blob downloads, or explicit cache: false / revalidating
    if (
      method !== 'get' ||
      config.cache === false ||
      config._revalidating === true ||
      config.responseType === 'blob' ||
      config.responseType === 'arraybuffer'
    ) {
      return config;
    }

    // 3. Normalized Cache Key
    const cacheKey = apiCache.generateKey(config.url, config.params);
    config._cacheKey = cacheKey;

    // 4. Stale-While-Revalidate (SWR) Check
    const { data: cachedData, isStale, hasData } = apiCache.getSWR(cacheKey);

    if (hasData) {
      // Background revalidation if data is stale
      if (isStale && !apiCache.inFlight.has(cacheKey)) {
        setTimeout(() => {
          api.get(config.url, {
            ...config,
            _revalidating: true,
            cache: false,
          }).then((res) => {
            apiCache.set(cacheKey, res.data, config.cacheTTL);
          }).catch(() => {});
        }, 20);
      }

      // Serve instantly from cache (0ms latency)
      config.adapter = () => Promise.resolve({
        data: cachedData,
        status: 200,
        statusText: isStale ? 'OK (FROM_CACHE_SWR_STALE)' : 'OK (FROM_CLIENT_CACHE)',
        headers: { 'x-client-cache': isStale ? 'STALE_SWR' : 'HIT' },
        config,
        request: {}
      });
      return config;
    }

    // 5. In-flight Deduplication: Share promise if identical request is in progress
    if (apiCache.inFlight.has(cacheKey)) {
      config.adapter = async () => {
        const inFlightResponse = await apiCache.inFlight.get(cacheKey);
        return {
          ...inFlightResponse,
          headers: { ...inFlightResponse.headers, 'x-client-cache': 'DEDUPLICATED' },
          config
        };
      };
      return config;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// ─── Response Interceptor: Caching + Invalidation + 401 Refresh ───────────────
api.interceptors.response.use(
  (response) => {
    const config = response.config || {};
    const method = (config.method || 'get').toLowerCase();

    // 1. Cache GET responses if they came from network (not cache hit)
    if (method === 'get' && config._cacheKey && response.headers?.['x-client-cache'] !== 'HIT') {
      apiCache.set(config._cacheKey, response.data, config.cacheTTL);
      apiCache.inFlight.delete(config._cacheKey);
    }

    // 2. Auto-invalidate cache on mutations (POST, PUT, PATCH, DELETE)
    if (['post', 'put', 'patch', 'delete'].includes(method)) {
      apiCache.invalidateByUrl(config.url);
    }

    return response;
  },
  async (error) => {
    const originalRequest = error.config || {};

    if (originalRequest._cacheKey) {
      apiCache.inFlight.delete(originalRequest._cacheKey);
    }

    // Handle 401 errors (unauthorized) - skip token refresh for login/registration/profile probe
    const isAuthRequest = originalRequest.url?.includes('/auth/login') ||
      originalRequest.url?.includes('/auth/register') ||
      originalRequest.url?.includes('/auth/profile') ||
      originalRequest.url?.includes('/auth/refresh-token');

    if (error.response?.status === 401 && !originalRequest._retry && !isAuthRequest) {
      originalRequest._retry = true;

      try {
        const response = await axios.post(
          `${api.defaults.baseURL}/auth/refresh-token`,
          {},
          { withCredentials: true }
        );

        const { token: newToken } = response.data?.data || {};
        if (newToken) {
          api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
        }

        return api(originalRequest);
      } catch (refreshError) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        apiCache.clear();
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export { apiCache };
export default api;
